'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '@/hen-ui/utils/index';
import { usePortalContainer } from '@/hen-ui/providers/PortalContainerContext';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex w-full items-center justify-between',
      'focus:outline-none',
      'disabled:cursor-not-allowed',
      '[&>span]:line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const scrollAffordanceHeightClass = 'h-6';

const selectContentMaxHeightClass =
  'max-h-[var(--radix-select-content-available-height,24rem)]';

const scrollAffordanceUpOverlayClass = cn(
  'pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-center text-neutral-500',
  scrollAffordanceHeightClass,
  'bg-gradient-to-b from-white via-white/80 to-transparent',
);

const scrollAffordanceDownOverlayClass = cn(
  'pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center justify-center text-neutral-500',
  scrollAffordanceHeightClass,
  'bg-gradient-to-t from-white via-white/80 to-transparent',
);

export function useViewportScrollAffordances(viewport: HTMLDivElement | null) {
  const [canScrollUp, setCanScrollUp] = React.useState(false);
  const [canScrollDown, setCanScrollDown] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!viewport) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      return;
    }

    const update = () => {
      const maxScroll = viewport.scrollHeight - viewport.clientHeight;
      const hasOverflow = maxScroll > 0;
      const scrollTop = Math.ceil(viewport.scrollTop);
      setCanScrollUp(hasOverflow && scrollTop > 0);
      setCanScrollDown(hasOverflow && scrollTop < maxScroll);
    };

    update();
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      update();
      raf2 = requestAnimationFrame(update);
    });

    viewport.addEventListener('scroll', update, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    resizeObserver?.observe(viewport);

    const mutationObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(update)
        : null;
    mutationObserver?.observe(viewport, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      viewport.removeEventListener('scroll', update);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [viewport]);

  return { canScrollUp, canScrollDown };
}

type SelectScrollAffordanceProps = {
  direction: 'up' | 'down';
  visible: boolean;
};

function SelectScrollAffordance({
  direction,
  visible,
}: SelectScrollAffordanceProps) {
  if (!visible) return null;

  const isUp = direction === 'up';
  const Icon = isUp ? ChevronUp : ChevronDown;

  return (
    <div
      aria-hidden
      data-testid={`select-scroll-affordance-${direction}`}
      className={
        isUp ? scrollAffordanceUpOverlayClass : scrollAffordanceDownOverlayClass
      }
    >
      <Icon className='h-4 w-4' />
    </div>
  );
}

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    {children ?? <ChevronUp className='h-4 w-4' />}
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    {children ?? <ChevronDown className='h-4 w-4' />}
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

type SelectViewportWithAffordancesProps = {
  children: React.ReactNode;
  viewportClassName?: string;
  position: 'item-aligned' | 'popper';
};

function SelectViewportWithAffordances({
  children,
  viewportClassName,
  position,
}: SelectViewportWithAffordancesProps) {
  const [viewport, setViewport] = React.useState<HTMLDivElement | null>(null);
  const { canScrollUp, canScrollDown } = useViewportScrollAffordances(viewport);

  const setViewportRef = React.useCallback((node: HTMLDivElement | null) => {
    setViewport(node);
  }, []);

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden',
        position === 'popper' &&
          cn(
            selectContentMaxHeightClass,
            'min-w-[var(--radix-select-trigger-width)]',
          ),
      )}
    >
      <SelectPrimitive.Viewport
        ref={setViewportRef}
        className={cn(
          selectContentMaxHeightClass,
          'overflow-y-auto',
          viewportClassName,
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollAffordance direction='up' visible={canScrollUp} />
      <SelectScrollAffordance direction='down' visible={canScrollDown} />
    </div>
  );
}

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    viewportClassName?: string;
  }
>(
  (
    { className, children, position = 'popper', viewportClassName, ...props },
    ref,
  ) => {
    const portalContainer = usePortalContainer();
    return (
      <SelectPrimitive.Portal container={portalContainer}>
        <SelectPrimitive.Content
          ref={ref}
          className={cn(
            'relative z-popover flex flex-col overflow-hidden',
            selectContentMaxHeightClass,
            className,
          )}
          position={position}
          {...props}
        >
          <SelectViewportWithAffordances
            position={position}
            viewportClassName={viewportClassName}
          >
            {children}
          </SelectViewportWithAffordances>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  },
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center',
      'outline-none',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(
      '-mx-1 my-1 h-px bg-neutral-100 dark:bg-neutral-800',
      className,
    )}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const SelectIcon = SelectPrimitive.Icon;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectIcon,
};
